import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import useSearchOptions from "../../../custom-hooks/useSearchOptions/useSearchOptions";
import CustomTablePagination from "../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { Props, State } from "./interfaces";
import useWindowSize from "../../../custom-hooks/useWindowSize/useWindowSize";
import { Page } from "../../../models/api/page";
import { SPACING } from "../../../_globalStore/redux/reducers/Theme/themeConstants";
import { CUSTOM_PAGINATION_ID } from "../../../reusable-components/Table/CustomTablePagination/utils";
import TableHeader from "../../../containers/TableHeader/TableHeader";
import QuickfiltersContainer from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { ExpenseReportView } from "./utils";
import { FT_EER, FT_MER } from "../../../utils/constants/filter-tables-constants";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { EmployeeExpenseReportsApiService } from "../../../services/employee-expense-reports/employee-expense-reports-api.service";
import { ExpenseReportsApiService } from "../../../services/expense-reports/expense-reports-api.service";
import ExpenseReportTable from "./ExpenseReportTable/ExpenseReportTable";
import { useSortByQuickFilter } from "../../../containers/Quickfilters/SortByQuickfilter/useSortByQuickFilter";
import { SortByQuickfilter } from "../../../containers/Quickfilters/SortByQuickfilter/SortByQuickfilter";
import {
  ExpenseReportStatusQuickfilter,
  useExpenseReportStatusQuickFilter,
} from "src/containers/Quickfilters/ExpenseReportStatusQuickfilter";
import ExportExpenseReport from "./ExportExpenseReport/ExportExpenseReport";
import AddEditExpenseReport from "./AddEditExpenseReport/AddEditExpenseReport";
import { ExpenseReportFilter } from "src/models/expense-reports";

const ExpenseReport = ({
  view = ExpenseReportView.employee,
  quickfilters,
  propsFilter,
  tableTitle,
  approversList,
  selectedLegalEntity,
  employee,
  readOnly = false,
  wholeReportIsReadOnly = false,
}: Props) => {
  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(
    view === ExpenseReportView.employee ? FT_EER : FT_MER
  );

  const { height } = useWindowSize();

  const { sortByQuickFilter, setSortByQuickFilter } = useSortByQuickFilter();
  const { expenseReportStatusQuickFilter, setExpenseReportStatusQuickFilter } = useExpenseReportStatusQuickFilter(
    view !== ExpenseReportView.employee
  );

  const [state, setState] = useState<State>({
    loading: false,
    items: [],
    totalItems: 0,
    error: "",
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
  });

  const handleSetPage = (field: keyof Page, value: Page[keyof Page]): void =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  const loadData = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setState((state) => ({
        ...state,
        loading: true,
      }));

      try {
        const service =
          view === ExpenseReportView.employee ? new EmployeeExpenseReportsApiService() : new ExpenseReportsApiService();

        const response = await service.loadItems(
          {
            ...propsFilter,
            expenseReportStatusIds: expenseReportStatusQuickFilter.expenseReportStatuses.expenseReportStatusIds,
          } as ExpenseReportFilter,
          searchOptions.query,
          sortByQuickFilter.sortByOptions.sortBy,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );

        if (!signal?.aborted) {
          if (!response.error) {
            setState((state) => ({
              ...state,
              items: response.items,
              totalItems: response.totalItems,
              loading: false,
              error: "",
            }));
          } else if (!signal?.aborted) {
            setState((state) => ({
              ...state,
              items: [],
              totalItems: 0,
              loading: false,
              error: response.error,
            }));
          }
        }

        !(response.error === "canceled" || response.isCanceled) && toastifyResponse(response, "failed");
      } catch (error) {
        if (!signal?.aborted) {
          setState((state) => ({
            ...state,
            loading: false,
            error: error as string,
          }));
        }
      }
    },
    [
      searchOptions,
      state.page,
      propsFilter,
      view,
      sortByQuickFilter.sortByOptions.sortBy,
      expenseReportStatusQuickFilter.expenseReportStatuses.expenseReportStatusIds,
    ]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    searchOptions.query.length === 0
      ? loadData(abortController.signal)
      : (timer = setTimeout(() => loadData(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000));

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [loadData, searchOptions.query, searchOptions.exactMatch]);

  useEffect(() => {
    if (state.page.pageSize * state.page.pageNumber >= state.totalItems && state.page.pageNumber > 0) {
      setState((state) => ({
        ...state,
        page: {
          pageNumber:
            state.page.pageSize * state.page.pageNumber === state.totalItems
              ? Math.floor(state.totalItems / state.page.pageSize) - 1
              : Math.floor(state.totalItems / state.page.pageSize),
          pageSize: state.page.pageSize,
        },
      }));
    }
  }, [state.totalItems, state.page]);

  const currHeight =
    height -
    (SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById(`${CUSTOM_PAGINATION_ID}`)?.clientHeight ?? 0) +
      40);

  return (
    <>
      <TableHeader
        tableTitle={tableTitle}
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        quickFilters={
          <QuickfiltersContainer>
            {quickfilters}
            {/* <SortByQuickfilter
              quickFilters={sortByQuickFilter}
              handleChangeQuickFilters={setSortByQuickFilter}
            /> */}
            <ExpenseReportStatusQuickfilter
              forManager={view !== ExpenseReportView.employee}
              quickFilters={expenseReportStatusQuickFilter}
              handleChangeQuickFilters={setExpenseReportStatusQuickFilter}
            />
            {!!approversList && approversList}
            <ExportExpenseReport
              disabled={state.loading || !state.items.length}
              items={state.items}
              propsFilter={propsFilter}
              view={view}
              selectedLegalEntity={selectedLegalEntity}
              employee={employee}
            />
          </QuickfiltersContainer>
        }
        buttonToRender={
          view === ExpenseReportView.employee && !readOnly && !wholeReportIsReadOnly ? (
            <AddEditExpenseReport
              effect={"add"}
              view={view}
              readOnly={readOnly}
              handleNotifyParent={loadData}
              parentIsLoading={state.loading}
              employee={employee}
            />
          ) : undefined
        }
      />

      <Grid sx={{ width: "100%", height: `${currHeight}px`, overflow: "auto", marginTop: "10px" }}>
        <LoadingOverlay loading={state.loading}>
          <ExpenseReportTable
            items={state.items}
            view={view}
            handleNotifyParent={loadData}
            parentIsLoading={state.loading}
            employee={employee}
            readOnly={readOnly}
            wholeReportIsReadOnly={wholeReportIsReadOnly}
          />
        </LoadingOverlay>
      </Grid>

      <CustomTablePagination
        totalItems={state.totalItems}
        pageNumber={state.page.pageNumber}
        handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
        pageSize={state.page.pageSize}
        handleChangePageSize={(s) => {
          handleSetPage("pageSize", s);
          handleSetPage("pageNumber", 0);
        }}
      />
    </>
  );
};

export default ExpenseReport;
